import { useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useTenantAnalytics, useNamespaceAnalytics, useNamespace } from '../lib/hooks'
import type { TenantAnalytics, NamespaceAnalytics } from '../lib/hooks'

// ── Tenant-level analytics (route: /analytics) ───────────────────────────────

export function Analytics() {
  const analytics = useTenantAnalytics()

  return (
    <Layout title="Analytics">
      <StatStrip
        total={analytics.data?.total_downloads ?? 0}
        last30={analytics.data?.downloads_last_30_days ?? 0}
        loading={analytics.loading}
      />

      {analytics.error && (
        <div className="empty-state" style={{ color: 'var(--red)' }}>{analytics.error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <DailySeries series={analytics.data?.daily_series ?? []} loading={analytics.loading} />
        <TopModulesCard modules={analytics.data?.top_modules ?? []} loading={analytics.loading} />
      </div>
    </Layout>
  )
}

// ── Namespace-level analytics (route: /namespaces/:slug/analytics) ────────────

export function NamespaceAnalytics() {
  const { slug = '' } = useParams()
  const ns            = useNamespace(slug)
  const analytics     = useNamespaceAnalytics(slug)

  return (
    <Layout title={`${slug} — Analytics`}>
      <StatStrip
        total={analytics.data?.total_downloads ?? 0}
        last30={analytics.data?.downloads_last_30_days ?? 0}
        loading={analytics.loading}
      />

      {ns.error && <div className="empty-state" style={{ color: 'var(--red)' }}>{ns.error}</div>}
      {analytics.error && (
        <div className="empty-state" style={{ color: 'var(--red)' }}>{analytics.error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <DailySeries series={analytics.data?.daily_series ?? []} loading={analytics.loading} />
        <TopModulesCard modules={analytics.data?.top_modules ?? []} loading={analytics.loading} />
      </div>

      {(analytics.data?.top_actors?.length ?? 0) > 0 && (
        <div className="card fu">
          <div className="card-header">
            <div>
              <div className="kicker">Activity</div>
              <h2 style={{ fontSize: '0.95rem' }}>Top actors</h2>
            </div>
          </div>
          <div className="table-wrap" style={{ borderRadius: 0 }}>
            <table>
              <thead>
                <tr><th>Actor</th><th>Type</th><th>Downloads</th></tr>
              </thead>
              <tbody>
                {analytics.data!.top_actors.map((a, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>
                      {a.actor_name ?? a.actor_id}
                    </td>
                    <td>
                      <span className={`badge ${a.actor_type === 'machine' ? 'badge-amber' : 'badge-neutral'}`}>
                        {a.actor_type}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text2)' }}>{a.download_count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  )
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function StatStrip({ total, last30, loading }: { total: number; last30: number; loading: boolean }) {
  return (
    <div className="stat-strip fu" style={{ marginBottom: 24 }}>
      <div className="stat-cell">
        <div className="stat-num">{loading ? '—' : total.toLocaleString()}</div>
        <div className="stat-label">Total downloads</div>
      </div>
      <div className="stat-cell">
        <div className="stat-num">{loading ? '—' : last30.toLocaleString()}</div>
        <div className="stat-label">Last 30 days</div>
      </div>
    </div>
  )
}

function TopModulesCard({
  modules,
  loading,
}: {
  modules: Array<TenantAnalytics['top_modules'][0] | NamespaceAnalytics['top_modules'][0]>
  loading: boolean
}) {
  return (
    <div className="card fu">
      <div className="card-header">
        <div>
          <div className="kicker">Popularity</div>
          <h2 style={{ fontSize: '0.95rem' }}>Top modules</h2>
        </div>
      </div>
      {loading && <div className="empty-state">Loading…</div>}
      {modules.map((m, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '11px 18px',
            borderBottom: '1px solid rgba(255,255,255,0.03)',
          }}
        >
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text)' }}>
              {m.namespace}/{m.name}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 2 }}>
              {m.provider}{m.latest_version ? ` · v${m.latest_version}` : ''}
            </div>
          </div>
          <div style={{ fontFamily: 'var(--head)', fontSize: '1rem', color: 'var(--accent)' }}>
            {m.download_count.toLocaleString()}
          </div>
        </div>
      ))}
      {!loading && modules.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">▲</div>
          <div>No downloads yet.</div>
        </div>
      )}
    </div>
  )
}

function DailySeries({
  series,
  loading,
}: {
  series: Array<{ date: string; count: number }>
  loading: boolean
}) {
  const max = Math.max(...series.map((s) => s.count), 1)

  return (
    <div className="card fu">
      <div className="card-header">
        <div>
          <div className="kicker">Trend</div>
          <h2 style={{ fontSize: '0.95rem' }}>Downloads (30 days)</h2>
        </div>
      </div>
      <div style={{ padding: '16px 18px' }}>
        {loading && <div className="empty-state">Loading…</div>}
        {!loading && series.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">▲</div>
            <div>No data yet.</div>
          </div>
        )}
        {!loading && series.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 3,
              height: 80,
            }}
          >
            {series.slice(-30).map((s) => (
              <div
                key={s.date}
                title={`${s.date}: ${s.count.toLocaleString()}`}
                style={{
                  flex: 1,
                  background: s.count > 0 ? 'var(--accent)' : 'var(--bg3)',
                  borderRadius: 2,
                  height: `${Math.max((s.count / max) * 100, s.count > 0 ? 4 : 2)}%`,
                  opacity: s.count > 0 ? 0.85 : 0.3,
                  cursor: 'default',
                  transition: 'opacity 0.1s',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
